import React, { useState } from "react";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import AccessControlForm from "../../components/settings/AccessControlForm";
import DeleteSettingsForm from "../../components/settings/DeleteSettingsForm";
import RecordDaysForm from "../../components/settings/RecordDaysForm";
import ReportInfoForm from "../../components/settings/ReportInfoForm";
import SizeUnitForm from "../../components/settings/SizeUnitForm";
import AccessDenied from "../../components/ui/AccessDenied";
import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { BreadcrumbType } from "../../types/common";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "સેટિંગ્સ" },
];

const tabListClass =
  "inline-flex h-10 max-w-full items-center justify-start overflow-x-auto rounded-md bg-[hsl(210_40%_96%)] p-1 text-[hsl(215_16%_47%)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

const tabTriggerClass =
  "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

const SettingsPage = () => {
  const loggingUser = useAppSelector(getLoggingUser);

  const canRead = hasPermission(loggingUser?.permissions, permission.settingsRead);
  const canManageDeletePin = hasPermission(
    loggingUser?.permissions,
    permission.settingsDeletePin
  );
  const canManageAccessTime = hasPermission(
    loggingUser?.permissions,
    permission.settingsAccessTime
  );
  const canManageReportInfo = hasPermission(
    loggingUser?.permissions,
    permission.settingsReportInfo
  );
  const canManageRecordDays = hasPermission(
    loggingUser?.permissions,
    permission.settingsRecordDays
  );
  const canManageSizeUnit = hasPermission(
    loggingUser?.permissions,
    permission.settingsSizeUnit
  );

  const [activeTab, setActiveTab] = useState<string>("delete-settings");

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className="font-semibold text-xl md:text-2xl text-[#113F67]">
          સેટિંગ્સ
        </h1>
      </div>

      {!canRead ? (
        <AccessDenied />
      ) : (
        <div className="flex flex-col gap-6">
          <div className="w-full">
            <div className={tabListClass}>
              {canManageDeletePin && (
                <button
                  type="button"
                  onClick={() => setActiveTab("delete-settings")}
                  className={`${tabTriggerClass} ${
                    activeTab === "delete-settings"
                      ? "bg-[#113F67] text-white shadow-sm"
                      : ""
                  }`}
                >
                  ડિલીટ સેટિંગ
                </button>
              )}

              {canManageAccessTime && (
                <button
                  type="button"
                  onClick={() => setActiveTab("access-control")}
                  className={`${tabTriggerClass} ${
                    activeTab === "access-control"
                      ? "bg-[#113F67] text-white shadow-sm"
                      : ""
                  }`}
                >
                  સિસ્ટમ એક્સેસ ટાઈમ
                </button>
              )}

              {canManageReportInfo && (
                <button
                  type="button"
                  onClick={() => setActiveTab("report-info")}
                  className={`${tabTriggerClass} ${
                    activeTab === "report-info"
                      ? "bg-[#113F67] text-white shadow-sm"
                      : ""
                  }`}
                >
                  PDF રિપોર્ટ
                </button>
              )}

              {canManageRecordDays && (
                <button
                  type="button"
                  onClick={() => setActiveTab("record-days")}
                  className={`${tabTriggerClass} ${
                    activeTab === "record-days"
                      ? "bg-[#113F67] text-white shadow-sm"
                      : ""
                  }`}
                >
                  રેકોર્ડ દિવસ
                </button>
              )}

              {canManageSizeUnit && (
                <button
                  type="button"
                  onClick={() => setActiveTab("size-unit")}
                  className={`${tabTriggerClass} ${
                    activeTab === "size-unit"
                      ? "bg-[#113F67] text-white shadow-sm"
                      : ""
                  }`}
                >
                  સાઈઝ યુનિટ
                </button>
              )}
            </div>

            <div className="mt-2">
              {activeTab === "delete-settings" &&
                (canManageDeletePin ? (
                  <DeleteSettingsForm />
                ) : (
                  <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                    <div className="p-6 py-10 text-center text-gray-500">
                      તમને ડિલીટ સેટિંગ્સ સંચાલિત કરવાની પરવાનગી નથી.
                    </div>
                  </div>
                ))}

              {activeTab === "access-control" &&
                (canManageAccessTime ? (
                  <AccessControlForm />
                ) : (
                  <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                    <div className="p-6 py-10 text-center text-gray-500">
                      તમને સિસ્ટમ એક્સેસ ટાઈમ સેટિંગ્સ સંચાલિત કરવાની પરવાનગી નથી.
                    </div>
                  </div>
                ))}

              {activeTab === "report-info" &&
                (canManageReportInfo ? (
                  <ReportInfoForm />
                ) : (
                  <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                    <div className="p-6 py-10 text-center text-gray-500">
                      તમને PDF રિપોર્ટ સેટિંગ્સ સંચાલિત કરવાની પરવાનગી નથી.
                    </div>
                  </div>
                ))}

              {activeTab === "record-days" &&
                (canManageRecordDays ? (
                  <RecordDaysForm />
                ) : (
                  <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                    <div className="p-6 py-10 text-center text-gray-500">
                      તમને રેકોર્ડ દિવસ સેટિંગ્સ સંચાલિત કરવાની પરવાનગી નથી.
                    </div>
                  </div>
                ))}

              {activeTab === "size-unit" &&
                (canManageSizeUnit ? (
                  <SizeUnitForm />
                ) : (
                  <div className="rounded-lg shadow-lg border-0 bg-white/80 backdrop-blur-sm">
                    <div className="p-6 py-10 text-center text-gray-500">
                      તમને સાઈઝ યુનિટ સેટિંગ્સ સંચાલિત કરવાની પરવાનગી નથી.
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};
export default SettingsPage;
